import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useMaps } from "../components/MapsProvider";
import RefreshButton from "../components/admin/RefreshButton";
import BookingModal from "../components/BookingModal";
import ViewBookingModal from "../components/ViewBookingModal";
import { Dialog } from "@headlessui/react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { formatDateShort } from '../utils/format';
import { PRIMARY_API_URL } from "../config/api";
import { supabase } from "../services/SupabaseService";

// Create a TableSkeleton component for loading states
const TableSkeleton = ({ rows = 5, columns = 6 }) => {
  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-full bg-black bg-opacity-60 rounded-xl overflow-hidden">
        <thead>
          <tr>
            {Array(columns).fill(0).map((_, i) => (
              <th key={i} className="p-3 text-left text-xs font-medium text-white uppercase">
                <Skeleton width={80} height={16} baseColor="#111" highlightColor="#222" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array(rows).fill(0).map((_, i) => (
            <tr key={i} className="border-t border-white/20">
              {Array(columns).fill(0).map((_, j) => (
                <td key={j} className="p-3 text-sm">
                  <Skeleton width={j === 0 ? 150 : 80} height={16} baseColor="#111" highlightColor="#222" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Add confirmation dialog component
const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message }) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md bg-[#0a0a0a] border border-white/30 rounded-xl shadow-lg p-6">
          <Dialog.Title className="text-xl font-bold text-white mb-4">
            {title}
          </Dialog.Title>
          
          <p className="text-gray-200 mb-6">
            {message}
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Delete
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

const statusOptions = ["pending", "confirmed", "completed", "cancelled"];
const statusColors = {
  confirmed: "bg-green-600 text-white",
  pending: "bg-white text-black",
  cancelled: "bg-red-600 text-white",
  completed: "bg-blue-600 text-white"
};

const TABS = [
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Past', value: 'past' }
];

const BookingsManagement = () => {
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const { isLoaded, forceLoaded } = useMaps();
  
  // Define bookingsPerPage constant
  const bookingsPerPage = 10;
  
  // Log maps status to debug
  useEffect(() => {
    console.log("Maps loading status in BookingsManagement:", { isLoaded, forceLoaded });
  }, [isLoaded, forceLoaded]);
  
  // Define fetchBookings function
  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${PRIMARY_API_URL}/api/bookings`);
      if (res.data && Array.isArray(res.data)) {
        setAllBookings(res.data);
        setFilteredBookings(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
      toast.error("Failed to fetch bookings");
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);
  
  // Apply filters and tab selection
  useEffect(() => {
    let result = [...allBookings];
    
    // Filter by search term
    if (search) {
      const lowercasedTerm = search.toLowerCase();
      result = result.filter(
        booking =>
          booking.full_name?.toLowerCase().includes(lowercasedTerm) ||
          booking.email?.toLowerCase().includes(lowercasedTerm) ||
          booking.pickup_name?.toLowerCase().includes(lowercasedTerm) ||
          booking.dropoff_name?.toLowerCase().includes(lowercasedTerm)
      );
    }
    
    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter(booking => booking.status === statusFilter);
    }
    
    // Filter by tab
    const now = new Date();
    if (activeTab === 'upcoming') {
      result = result.filter(booking => new Date(booking.datetime) >= now);
    } else if (activeTab === 'past') {
      result = result.filter(booking => new Date(booking.datetime) < now);
    }
    
    // Sort bookings chronologically from earliest to latest
    result.sort((a, b) => {
      const dateA = new Date(a.datetime);
      const dateB = new Date(b.datetime);
      return dateA - dateB;
    });
    
    setFilteredBookings(result);
    setCurrentPage(1);
  }, [search, statusFilter, allBookings, activeTab]);
  
  // Pagination
  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);
  
  // Handle status change
  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      // Use the general booking update endpoint instead of a specific status endpoint
      const response = await axios.put(`${PRIMARY_API_URL}/api/bookings/${bookingId}`, {
        status: newStatus
      });
      
      if (response.status === 200) {
        // Update local state
        setAllBookings(prevBookings => prevBookings.map(booking => 
          booking.id === bookingId ? { ...booking, status: newStatus } : booking
        ));
        
        toast.success(`Booking status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error("Failed to update booking status:", error);
      toast.error("Failed to update status. Please try again.");
    }
  };

  // Handle new booking creation
  const handleCreateBooking = async (newBooking) => {
    try {
      console.log('📝 Admin creating new booking:', newBooking);
      
      // Use the same robust API endpoint as guest bookings
      // This includes all availability checks, van assignments, capacity validation, etc.
      const response = await axios.post(`${PRIMARY_API_URL}/api/bookings`, {
        fullName: newBooking.full_name,
        email: newBooking.email,
        phone: newBooking.phone,
        passengers: newBooking.passengers,
        luggage: newBooking.luggage,
        pickup: {
          name: newBooking.pickup_name,
          position: { 
            lat: newBooking.pickup_lat, 
            lng: newBooking.pickup_lng 
          }
        },
        dropoff: {
          name: newBooking.dropoff_name,
          position: { 
            lat: newBooking.dropoff_lat, 
            lng: newBooking.dropoff_lng 
          }
        },
        datetime: newBooking.datetime,
        price: newBooking.price,
        base_price: newBooking.base_price,
        distance: newBooking.distance,
        duration: newBooking.duration,
        price_manually_set: newBooking.price_manually_set,
        vans_needed: newBooking.vans_needed,
        auto_assign: true, // Auto-assign available vans
        status: newBooking.status || "pending"
      });

      if (response.data.success) {
        console.log('✅ Admin booking created successfully:', response.data.booking);
        
        // Add new booking to state
        setAllBookings(prev => [...prev, response.data.booking]);
        setIsNewModalOpen(false);
        toast.success("Booking created successfully with van assignments!");
      } else {
        const errorMsg = response.data.error || "Failed to create booking";
        console.error("❌ Admin booking creation failed:", errorMsg);
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }
      
    } catch (error) {
      console.error("Failed to create admin booking:", error);
      
      // Handle specific error cases from the robust API
      if (error.response?.data?.error) {
        const errorMsg = error.response.data.error;
        toast.error(errorMsg);
        throw new Error(errorMsg);
      } else if (error.response?.status === 400) {
        const errorMsg = "Booking validation failed - please check availability and capacity";
        toast.error(errorMsg);
        throw new Error(errorMsg);
      } else if (error.response?.status === 500) {
        toast.error("Server error occurred. Please try again.");
        throw new Error("Server error occurred");
      } else {
        toast.error("Failed to create booking. Please try again.");
        throw new Error("Failed to create booking");
      }
    }
  };

  // Handle booking update
  const handleUpdateBooking = async (updatedBooking) => {
    try {
      console.log('📝 Updating booking:', updatedBooking);
      const res = await axios.put(`${PRIMARY_API_URL}/api/bookings/${updatedBooking.id}`, updatedBooking);
      
      // Update local state
      setAllBookings(allBookings.map(booking => 
        booking.id === updatedBooking.id ? updatedBooking : booking
      ));
      
      setIsEditModalOpen(false);
      toast.success("Booking updated successfully!");
    } catch (error) {
      console.error("Failed to update booking:", error);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || "Invalid booking data";
        toast.error(errorMsg);
      } else if (error.response?.status === 500) {
        toast.error("Server error occurred. Please try again.");
      } else {
        toast.error("Failed to update booking. Please try again.");
      }
      
      // Re-throw the error so the BookingModal can handle it
      throw error;
    }
  };

  // Handle booking deletion
  const handleDeleteBooking = async (id) => {
    console.log("🗑️ handleDeleteBooking called with ID:", id);
    
    const token = localStorage.getItem("admin_token");
    console.log("🔑 Auth token:", token ? "Found" : "Missing");
    
    if (!token) {
      console.error("❌ No authentication token found");
      toast.error("Authentication required");
      return;
    }
    
    // Create explicit headers for this request
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
    };
    
    console.log("📡 API URL:", `${PRIMARY_API_URL}/api/bookings/${id}`);
    console.log("📡 Headers:", headers);
    
    setIsLoading(true);
    
    try {
      console.log(`🗑️ Starting deletion process for booking ${id}...`);
      
      // First, delete any assignments for this booking
      try {
        console.log(`🔗 Deleting assignments for booking ${id}...`);
        const assignmentResponse = await axios.delete(`${PRIMARY_API_URL}/api/bookings/${id}/assignments`, { headers });
        console.log(`✅ Assignment deletion response:`, assignmentResponse.data);
      } catch (assignmentError) {
        // Log but don't fail if assignments don't exist
        console.log(`ℹ️ No assignments to delete for booking ${id}:`, assignmentError.response?.data?.error || assignmentError.message);
      }
      
      // Then delete the booking itself
      console.log(`🗑️ Deleting booking ${id}...`);
      const response = await axios.delete(`${PRIMARY_API_URL}/api/bookings/${id}`, { headers });
      console.log(`✅ Delete response:`, response.data);
      
      if (response.status === 200) {
        toast.success(`Booking deleted successfully`);
        
        // Update local state immediately
        setAllBookings(prev => {
          const updated = prev.filter(booking => booking.id !== id);
          console.log(`📊 Updated allBookings: ${prev.length} -> ${updated.length} bookings`);
          return updated;
        });
        setFilteredBookings(prev => {
          const updated = prev.filter(booking => booking.id !== id);
          console.log(`📊 Updated filteredBookings: ${prev.length} -> ${updated.length} bookings`);
          return updated;
        });
        
        console.log(`✅ Successfully deleted booking ${id}`);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error deleting booking:", error);
      console.error("❌ Error response:", error.response);
      
      let errorMessage = "Failed to delete booking. Please try again.";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        errorMessage = "Booking not found. It may have already been deleted.";
        // Still remove from local state if it was already deleted
        setAllBookings(prev => prev.filter(booking => booking.id !== id));
        setFilteredBookings(prev => prev.filter(booking => booking.id !== id));
        console.log(`📊 Removed booking ${id} from local state (not found on server)`);
      }
      
      toast.error(errorMessage);
    } finally {
      console.log("🏁 Delete operation completed");
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setBookingToDelete(null);
    }
  };
  
  // Show confirmation dialog - handle both booking objects and IDs
  const confirmDelete = (bookingOrId) => {
    console.log("🗑️ confirmDelete called with:", bookingOrId);
    
    // If it's just an ID, find the booking object
    if (typeof bookingOrId === 'number' || typeof bookingOrId === 'string') {
      const booking = allBookings.find(b => b.id === parseInt(bookingOrId));
      if (booking) {
        console.log("📋 Found booking by ID:", booking);
        setBookingToDelete(booking);
        setIsDeleteModalOpen(true);
      } else {
        console.error("❌ Booking not found for ID:", bookingOrId);
        toast.error("Booking not found");
      }
    } else {
      // It's already a booking object
      console.log("📋 Using booking object:", bookingOrId);
      setBookingToDelete(bookingOrId);
      setIsDeleteModalOpen(true);
    }
  };
  
  const formatPrice = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR"
  }).format(value);
  
  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Bookings Management</h1>
          <p className="text-gray-400">View and manage all your customer bookings</p>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <RefreshButton 
            onClick={fetchBookings}
            isLoading={isLoading}
          />
          <button
            onClick={() => setIsNewModalOpen(true)}
            className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add New Booking
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-white mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by name, email or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-2 bg-black border border-white/20 rounded focus:border-white text-white"
            />
          </div>
          
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-white mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-2 bg-black border border-white/20 rounded focus:border-white text-white"
            >
              <option value="all">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Tabs for Upcoming/Past */}
      <div className="flex space-x-4 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.value}
            className={`px-5 py-2 rounded-t-lg font-semibold border-b-2 transition-colors duration-150 ${
              activeTab === tab.value 
                ? 'border-white text-white bg-black' 
                : 'border-transparent text-gray-400 bg-transparent hover:text-white'
            }`}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Bookings Table */}
      <div className="bg-[#121212] rounded-xl shadow-lg border border-white/20 overflow-hidden">
        {isLoading ? (
          <TableSkeleton rows={7} columns={7} />
        ) : filteredBookings.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-black/30">
                    <th className="px-4 py-3 text-left text-white">Customer</th>
                    <th className="px-4 py-3 text-left text-white">Route</th>
                    <th className="px-4 py-3 text-left text-white hidden md:table-cell">Date & Time</th>
                    <th className="px-4 py-3 text-left text-white hidden md:table-cell">Price</th>
                    <th className="px-4 py-3 text-left text-white">Status</th>
                    <th className="px-4 py-3 text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBookings.map(booking => (
                    <tr key={booking.id} className="border-t border-white/10 hover:bg-black/20">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium">{booking.full_name}</div>
                          <div className="text-gray-400 text-sm">{booking.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm">From: {booking.pickup_name}</span>
                          <span className="text-sm">To: {booking.dropoff_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {formatDateShort(booking.datetime)}
                      </td>
                      <td className="px-4 py-3 font-medium hidden md:table-cell">
                        {formatPrice(booking.price)}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={booking.status}
                          onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                          className={`px-2 py-1 rounded-full text-xs ${statusColors[booking.status]} border-0 focus:ring-1 focus:ring-white`}
                        >
                          {statusOptions.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setIsViewModalOpen(true);
                          }}
                          className="text-white hover:text-gray-300 mr-2"
                        >
                          View
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setIsEditModalOpen(true);
                          }}
                          className="text-white hover:text-gray-300"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 flex justify-center border-t border-white/10">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-800 text-gray-500' : 'bg-black text-white hover:bg-white/10'}`}
                  >
                    Prev
                  </button>
                  
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`px-3 py-1 rounded ${currentPage === idx + 1 ? 'bg-white text-black' : 'bg-black text-white hover:bg-white/10'}`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-800 text-gray-500' : 'bg-black text-white hover:bg-white/10'}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-400">
            No bookings found with the current filters
          </div>
        )}
      </div>
      
      {/* Booking Modals */}
      {/* View Booking Modal */}
      {selectedBooking && (
        <ViewBookingModal
          booking={selectedBooking}
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          onEdit={(booking) => {
            setIsViewModalOpen(false);
            setIsEditModalOpen(true);
          }}
          onDelete={confirmDelete}
        />
      )}
      
      {/* Edit Booking Modal */}
      {selectedBooking && (
        <BookingModal
          booking={selectedBooking}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleUpdateBooking}
        />
      )}
      
      {/* New Booking Modal */}
      <BookingModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onSave={handleCreateBooking}
      />
      
      {/* Add confirmation dialog */}
      <ConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => handleDeleteBooking(bookingToDelete?.id)}
        title="Delete Booking"
        message={`Are you sure you want to delete the booking for ${bookingToDelete?.full_name}? This action cannot be undone.`}
      />
    </div>
  );
};

export default BookingsManagement; 